// Model-only rebalance worksheet (round 7 #4). Turns actual-vs-target allocation
// drift into "how many dollars need to move" per asset class. Pure + testable;
// no order execution — this is preparation for advice, not a trade instruction.

export interface RebalanceRow {
  bucket: string;
  actualPct: number;   // 0..1
  targetPct: number;   // 0..1
  driftPct: number;    // actual - target
  currentValue: number; // dollars
  targetValue: number;  // dollars
  tradeValue: number;   // dollars, + = buy, - = sell, 0 = within band
  action: 'buy' | 'sell' | 'hold';
}

export function buildRebalanceRows(input: {
  totalValue: number;                       // total portfolio value incl cash (dollars)
  actual: Record<string, number>;           // bucket -> actual fraction 0..1
  target: Record<string, number>;           // bucket -> target fraction 0..1
  minTradeDollars?: number;                  // ignore trades below this (default 500)
}): RebalanceRow[] {
  const minTrade = input.minTradeDollars ?? 500;
  const buckets = new Set<string>([...Object.keys(input.actual), ...Object.keys(input.target)]);
  const rows: RebalanceRow[] = [];
  for (const bucket of buckets) {
    const actualPct = input.actual[bucket] ?? 0;
    const targetPct = input.target[bucket];
    if (targetPct == null) continue; // only rebalance buckets with a target set
    const currentValue = round2(actualPct * input.totalValue);
    const targetValue = round2(targetPct * input.totalValue);
    const raw = round2(targetValue - currentValue);
    const tradeValue = Math.abs(raw) < minTrade ? 0 : raw;
    rows.push({
      bucket, actualPct, targetPct,
      driftPct: round4(actualPct - targetPct),
      currentValue, targetValue, tradeValue,
      action: tradeValue > 0 ? 'buy' : tradeValue < 0 ? 'sell' : 'hold',
    });
  }
  return rows.sort((a, b) => Math.abs(b.tradeValue) - Math.abs(a.tradeValue));
}

function round2(n: number) { return Math.round(n * 100) / 100; }
function round4(n: number) { return Math.round(n * 10000) / 10000; }
